import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@pagespeedify/ui';

interface ResizableSplitLayoutProps {
    leftNode: React.ReactNode;
    rightNode: React.ReactNode;
    initialLeftWidth?: number; // percentage (0-100)
    minLeftWidth?: number; // percentage (0-100)
    maxLeftWidth?: number; // percentage (0-100)
    className?: string;
}

export default function ResizableSplitLayout({
    leftNode,
    rightNode,
    initialLeftWidth = 60,
    minLeftWidth = 20,
    maxLeftWidth = 80,
    className
}: ResizableSplitLayoutProps) {
    const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const relativeX = e.clientX - containerRect.left;

        // Calculate percentage
        let newWidthPercent = (relativeX / containerWidth) * 100;

        // Apply limits
        if (newWidthPercent < minLeftWidth) newWidthPercent = minLeftWidth;
        if (newWidthPercent > maxLeftWidth) newWidthPercent = maxLeftWidth;

        setLeftWidth(newWidthPercent);
    }, [isDragging, minLeftWidth, maxLeftWidth]);

    // Global event listeners for drag
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            // Disable text selection while dragging
            document.body.style.userSelect = 'none';
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <div
            ref={containerRef}
            className={cn("flex w-full overflow-hidden", className)}
        >
            {/* Left Panel */}
            <div
                style={{ width: `${leftWidth}%` }}
                className="h-full min-w-0 min-h-0 flex flex-col"
            >
                {leftNode}
            </div>

            {/* Drag Handle */}
            <div
                className={cn(
                    "w-1.5 hover:w-2 -ml-0.5 -mr-0.5 z-10 cursor-col-resize flex flex-col justify-center items-center group transition-all duration-150 select-none",
                    isDragging ? "bg-primary/50 w-2" : "hover:bg-primary/20"
                )}
                onMouseDown={handleMouseDown}
            >
                {/* Visual indicator (vertical line) */}
                <div className={cn(
                    "w-0.5 h-full bg-border transition-colors",
                    isDragging ? "bg-primary" : "group-hover:bg-primary/50"
                )} />

                {/* Visual handle (pill) */}
                <div className={cn(
                    "absolute h-8 w-1.5 rounded-full bg-border transition-all",
                    isDragging ? "bg-primary h-12" : "group-hover:bg-primary group-hover:h-10"
                )} />
            </div>

            {/* Right Panel */}
            <div className="flex-1 h-full min-w-0 min-h-0 flex flex-col">
                {rightNode}
            </div>
        </div>
    );
}
